import { useEffect, useRef } from "react";
import { motion, useMotionValue, useTransform } from "framer-motion";

export default function StackSection({ children, z = 1 }) {
  const ref = useRef(null);
  const flowTop = useRef(0);
  const progress = useMotionValue(0);

  const scale = useTransform(progress, [0, 1], [1, 0.93]);
  const brightness = useTransform(progress, [0, 1], [1, 0.8]);
  const filter = useTransform(brightness, (v) => `brightness(${v})`);
  const radius = useTransform(progress, [0, 1], [0, 36]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const measure = () => {
      flowTop.current = el.offsetTop;
      el.style.top = `${Math.min(0, window.innerHeight - el.offsetHeight)}px`;
    };

    const onScroll = () => {
      const h = el.offsetHeight;
      const vh = window.innerHeight;
      const start = flowTop.current + h - vh;
      const end = flowTop.current + h;
      const p = Math.min(1, Math.max(0, (window.scrollY - start) / Math.max(1, end - start)));
      progress.set(p);
    };

    measure();
    onScroll();
    const ro = new ResizeObserver(() => {
      measure();
      onScroll();
    });
    ro.observe(el);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", onScroll);
    };
  }, [progress]);

  return (
    <div ref={ref} className="sticky" style={{ top: 0, zIndex: z }}>
      <motion.div
        style={{ scale, filter, borderRadius: radius, transformOrigin: "center top", overflow: "hidden" }}
        data-testid="stack-section-inner"
      >
        {children}
      </motion.div>
    </div>
  );
}
